/*
 * PDP "Traditional retail" info modal — Element Brooklyn.
 *
 * The info icon next to the struck-through traditional-retail price opens a
 * modal explaining how that price + the savings % were derived: our product
 * (volume + price) vs the luxury comparator (per-bottle volume + price, plus
 * the volume-normalisation factor when our size is larger). For bundles, one
 * row per constituent.
 *
 * Data is fetched lazily on first open from the element-ops App Proxy:
 *   /apps/api/compare-details/<handle>?country=<US|GB|AU>&variant=<id>
 * (see Api::V1::CompareDetailsController). Same-origin, HMAC-signed by Shopify.
 * The whole thing degrades gracefully — if the fetch fails, the modal shows a
 * short fallback message; the strikethrough price is unaffected.
 */
(function () {
  if (window.__pdpCompareBreakdownInit) return;
  window.__pdpCompareBreakdownInit = true;

  var PROXY_BASE = '/apps/api/compare-details/';
  var STATIC_DISCLAIMER = 'Prices are subject to change and may vary across retailers.';
  var cache = {};
  var modal = null;
  var lastFocused = null;

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function buildModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'cmp-modal';
    modal.setAttribute('hidden', '');
    modal.innerHTML =
      '<div class="cmp-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="cmp-modal-title">' +
        '<button type="button" class="cmp-modal__close modal__close-button link link--text focus-inset" data-cmp-close aria-label="Close">' +
          '<svg class="icon icon-close" aria-hidden="true" focusable="false"><use href="#icon-close"></use></svg>' +
        '</button>' +
        '<h2 class="cmp-modal__title" id="cmp-modal-title">How our pricing compares</h2>' +
        '<div class="cmp-modal__body" data-cmp-body></div>' +
      '</div>';
    document.body.appendChild(modal);

    modal.addEventListener('click', function (e) {
      // Close on the × button or any click on the backdrop (outside the dialog).
      if (e.target.closest('[data-cmp-close]') || !e.target.closest('.cmp-modal__dialog')) {
        close();
      }
    });
    return modal;
  }

  function open() {
    lastFocused = document.activeElement;
    buildModal().removeAttribute('hidden');
    document.documentElement.classList.add('cmp-modal-open');
    var closeBtn = modal.querySelector('.cmp-modal__close');
    if (closeBtn) closeBtn.focus();
    document.addEventListener('keydown', onKeydown);
  }

  function close() {
    if (!modal) return;
    modal.setAttribute('hidden', '');
    document.documentElement.classList.remove('cmp-modal-open');
    document.removeEventListener('keydown', onKeydown);
    if (lastFocused && lastFocused.focus && document.contains(lastFocused)) lastFocused.focus();
  }

  // Tab and Shift+Tab cycle within the open dialog instead of reaching the page
  // behind it. The focusable elements are read on each press because the body
  // renders after the fetch. (The theme's trapFocus helper doesn't fit here: it
  // reads the list once, and calling it again while focus is already inside
  // leaves Tab unhandled.)
  function onKeydown(e) {
    if (e.key === 'Escape' || e.keyCode === 27) { close(); return; }
    if (e.key !== 'Tab') return;
    var dialog = modal.querySelector('.cmp-modal__dialog');
    var items = Array.prototype.filter.call(
      dialog.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null; }
    );
    if (!items.length) return;
    var active = document.activeElement;
    var inside = dialog.contains(active);
    if (e.shiftKey && (!inside || active === items[0])) {
      e.preventDefault();
      items[items.length - 1].focus();
    } else if (!e.shiftKey && (!inside || active === items[items.length - 1])) {
      e.preventDefault();
      items[0].focus();
    }
  }

  function setBody(html) {
    var body = modal.querySelector('[data-cmp-body]');
    if (body) body.innerHTML = html;
  }

  function disclaimerBlock(affiliation) {
    var text = affiliation ? affiliation + ' ' + STATIC_DISCLAIMER : STATIC_DISCLAIMER;
    return '<p class="cmp-disclaimer">' + esc(text) + '</p>';
  }

  function disclaimers(data) {
    return disclaimerBlock(data && data.disclaimer);
  }

  /* The volume-normalisation factor, to 1 dp. Derived from the two figures the
     sentence reconciles (retail total ÷ per-bottle price) so the copy can never
     contradict them; the API's own value is the fallback. Ops used to send this
     rounded to a whole number, which rendered 17 oz vs a 12 oz bottle — a 1.4×
     ratio — as a nonsensical "about 1× … so we compare against $127.00". */
  function multiplierLabel(theirs) {
    var total = parseFloat(theirs.normalized_total_amount);
    var unit = parseFloat(theirs.unit_price_amount);
    if (isFinite(total) && isFinite(unit) && unit > 0) {
      var m = Math.round((total / unit) * 10) / 10;
      if (m > 0) return String(m);
    }
    return theirs.count == null ? '' : String(theirs.count);
  }

  function normalizationLine(ours, theirs) {
    if (!theirs || !theirs.normalized) return '';
    /* Deliberately not phrased as "(count × unit_price)": the factor is usually
       fractional and the retail total is floored, so the literal multiplication
       never lands on the number above it. State the per-bottle rate instead. */
    return (
      '<p class="cmp-note cmp-note--norm">Our ' + esc(ours.volume) + ' is about ' + esc(multiplierLabel(theirs)) +
      '× a ' + esc(theirs.unit_volume) + ' bottle of ' + esc(theirs.brand) + ' — matching that volume at ' +
      esc(theirs.unit_price) + ' a bottle costs ' + esc(theirs.normalized_total) + '.</p>'
    );
  }

  function renderSingle(data, marketLabel) {
    var ours = data.ours || {};
    var theirs = data.theirs || {};
    var html = disclaimers(data);
    html +=
      '<table class="cmp-table"><thead><tr>' +
        '<th scope="col"></th><th scope="col">Volume</th><th scope="col">Price</th>' +
      '</tr></thead><tbody>' +
        '<tr class="cmp-row--ours"><th scope="row">' + esc(ours.title) + '</th>' +
          '<td>' + esc(ours.volume) + '</td><td>' + esc(ours.price) + '</td></tr>' +
        '<tr class="cmp-row--theirs"><th scope="row"><span class="cmp-eyebrow">' + esc(marketLabel) + '</span>' +
          esc(theirs.brand) + '</th>' +
          '<td>' + esc(theirs.unit_volume) + '</td><td>' + esc(theirs.unit_price) + '</td></tr>' +
      '</tbody></table>';
    html += normalizationLine(ours, theirs);
    if (data.savings_pct != null) {
      html += '<p class="cmp-savings">You save <strong>' + esc(data.savings_pct) + '%</strong> vs traditional retail (' +
        esc(theirs.normalized_total) + ').</p>';
    }
    return html;
  }

  function renderBundle(data, marketLabel) {
    var comps = data.components || [];
    var ours = data.ours || {};
    var theirs = data.theirs || {};
    var html = disclaimers(data);
    html += '<table class="cmp-table cmp-table--bundle"><thead><tr>' +
      '<th scope="col">In this set</th><th scope="col">Volume</th><th scope="col">Price</th>' +
      '</tr></thead><tbody>';

    comps.forEach(function (c) {
      var co = c.ours || {};
      var ct = c.theirs || {};
      var qty = c.quantity && c.quantity > 1 ? ' <span class="cmp-qty">×' + esc(c.quantity) + '</span>' : '';
      html +=
        '<tr class="cmp-row--ours cmp-row--group"><th scope="row">' + esc(co.title) + qty + '</th>' +
          '<td>' + esc(co.volume) + '</td><td>' + esc(co.price) + '</td></tr>' +
        '<tr class="cmp-row--theirs"><th scope="row"><span class="cmp-arrow">↳</span>' +
          '<span class="cmp-eyebrow">' + esc(marketLabel) + '</span>' + esc(ct.brand) +
          (ct.normalized ? ' <span class="cmp-qty">(×' + esc(multiplierLabel(ct)) + ')</span>' : '') + '</th>' +
          '<td>' + esc(ct.unit_volume) + '</td><td>' + esc(ct.normalized_total) + '</td></tr>';
    });

    html += '</tbody><tfoot>' +
      '<tr class="cmp-row--total cmp-row--retailtotal"><th scope="row">Traditional retail total</th><td></td><td>' +
        esc(theirs.normalized_total) + '</td></tr>' +
      '<tr class="cmp-row--total cmp-row--ourtotal"><th scope="row">Our price</th><td></td><td>' +
        esc(ours.price) + '</td></tr>' +
      '</tfoot></table>';

    if (data.savings_pct != null) {
      html += '<p class="cmp-savings">You save <strong>' + esc(data.savings_pct) + '%</strong> vs traditional retail.</p>';
    }
    return html;
  }

  function render(data, marketLabel, fallback) {
    if (!data || data.ok === false) {
      setBody(renderFallback(fallback || {}, marketLabel));
      return;
    }
    setBody(data.is_bundle ? renderBundle(data, marketLabel) : renderSingle(data, marketLabel));
  }

  /* Shown when the ops API is unreachable/slow/errors. Built entirely from
     server-rendered data attributes on the trigger, so the modal still gives
     the headline comparison + both disclaimers attractively — never an error. */
  function renderFallback(fb, marketLabel) {
    var html = disclaimerBlock(fb.disclaimer);
    if (fb.brand) {
      html += '<p class="cmp-fallback-brand"><span class="cmp-eyebrow">' + esc(marketLabel) + '</span>' +
        esc(fb.brand) + '</p>';
    }
    if (fb.ourPrice || fb.retail || fb.savings) {
      html += '<div class="cmp-figures">';
      if (fb.ourPrice) {
        html += '<div class="cmp-fig"><span class="cmp-fig__label">Our price</span>' +
          '<span class="cmp-fig__val">' + esc(fb.ourPrice) + '</span></div>';
      }
      if (fb.retail) {
        // Mirrors the PDP trigger's label, which reads "Retail adjusted for price/oz"
        // on products whose retail figure was scaled to our volume.
        html += '<div class="cmp-fig"><span class="cmp-fig__label">' + esc(fb.retailLabel || 'Traditional retail') + '</span>' +
          '<span class="cmp-fig__val cmp-fig__val--strike">' + esc(fb.retail) + '</span></div>';
      }
      if (fb.savings) {
        html += '<div class="cmp-fig cmp-fig--save"><span class="cmp-fig__label">You save</span>' +
          '<span class="cmp-fig__val">' + esc(fb.savings) + '%</span></div>';
      }
      html += '</div>';
    }
    return html;
  }

  function fetchData(handle, country, variant) {
    var key = handle + '|' + country + '|' + variant;
    if (cache[key]) return Promise.resolve(cache[key]);

    var url = PROXY_BASE + encodeURIComponent(handle) + '?country=' + encodeURIComponent(country);
    if (variant) url += '&variant=' + encodeURIComponent(variant);

    // Bail out fast if ops is slow/unresponsive so the modal never hangs on "Loading…".
    var controller = 'AbortController' in window ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, 6000) : null;
    var opts = { headers: { Accept: 'application/json' } };
    if (controller) opts.signal = controller.signal;

    return fetch(url, opts)
      .then(function (r) { if (timer) clearTimeout(timer); return r.ok ? r.json() : null; })
      .then(function (json) { if (json && json.ok !== false) cache[key] = json; return json; });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest ? e.target.closest('[data-compare-breakdown-trigger]') : null;
    if (!trigger) return;
    e.preventDefault();

    var handle = trigger.getAttribute('data-handle');
    var country = trigger.getAttribute('data-country') || 'US';
    var variant = trigger.getAttribute('data-variant') || '';
    var marketLabel = trigger.getAttribute('data-market-label') || 'Compare to';
    if (!handle) return;

    var fallback = {
      brand: trigger.getAttribute('data-fallback-brand') || '',
      disclaimer: trigger.getAttribute('data-fallback-disclaimer') || '',
      ourPrice: trigger.getAttribute('data-fallback-our-price') || '',
      retail: trigger.getAttribute('data-fallback-retail') || '',
      retailLabel: trigger.getAttribute('data-fallback-retail-label') || '',
      savings: trigger.getAttribute('data-fallback-savings') || ''
    };

    open();
    setBody('<p class="cmp-note cmp-loading">Loading comparison…</p>');

    try {
      fetchData(handle, country, variant)
        .then(function (data) { render(data, marketLabel, fallback); })
        .catch(function () { render(null, marketLabel, fallback); });
    } catch (err) {
      render(null, marketLabel, fallback);
    }
  });

  // The trigger is a span with role="button" — make Enter/Space open it too.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    var trigger = e.target.closest ? e.target.closest('[data-compare-breakdown-trigger]') : null;
    if (!trigger) return;
    e.preventDefault();
    trigger.click();
  });
})();
